<template>
  <div class="auth-login">
    <AuthViewIntro
      :title="$t('profile.login.welcome-back')"
      :subtitle="$t('profile.login.sub-heading')" />

    <AuthAlert v-if="message" :message="message" variant="info" />
    <AuthAlert v-if="errorMessage" :message="errorMessage" />

    <AuthTextField
      v-model="form.email"
      type="email"
      autocomplete="email"
      :label="$t('profile.login.email')"
      placeholder="user@guestaps.com"
      :error="emailError"
      @blur="touched.email = true" />

    <AuthTextField
      v-model="form.password"
      type="password"
      autocomplete="current-password"
      :label="$t('profile.login.password')"
      :error="passwordError"
      @blur="touched.password = true"
      @enter="submit">
      <template #label-action>
        <button type="button" class="auth-login__link" @click="$emit('switch-view', 'forgot-password')">
          {{ $t('profile.forgot-password') }}
        </button>
      </template>
    </AuthTextField>

    <AuthSubmitButton
      class="auth-login__submit"
      :disabled="!isFormValid"
      :loading="isSubmitting"
      @click="submit">
      {{ $t('profile.login.login') }}
    </AuthSubmitButton>

    <div class="auth-login__divider">
      <span>{{ $t('profile.not-register') }}?</span>
    </div>

    <AuthSubmitButton variant="outline" @click="$emit('switch-view', 'register')">
      {{ $t('profile.create-account') }}
    </AuthSubmitButton>
  </div>
</template>

<script>
  const { email, required } = require('vuelidate/lib/validators');
  import AuthViewIntro from '../AuthViewIntro.vue';
  import AuthTextField from '../AuthTextField.vue';
  import AuthSubmitButton from '../AuthSubmitButton.vue';
  import AuthAlert from '../AuthAlert.vue';
  import { loginRequest } from '@/utils/authApi.js';
  import { applyAuthSuccess } from '@/utils/authSession.js';

  export default {
    name: 'LoginView',
    components: {
      AuthViewIntro,
      AuthTextField,
      AuthSubmitButton,
      AuthAlert
    },
    props: {
      message: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        form: {
          email: '',
          password: ''
        },
        touched: {
          email: false,
          password: false
        },
        errorMessage: '',
        isSubmitting: false
      }
    },
    validations: {
      form: {
        email: { required, email },
        password: { required }
      }
    },
    computed: {
      isFormValid() {
        return !this.$v.form.$invalid;
      },
      emailError() {
        if (!this.touched.email) return '';
        if (!this.$v.form.email.required) return this.$t('profile.auth.required-field');
        if (!this.$v.form.email.email) return this.$t('profile.auth.invalid-email');
        return '';
      },
      passwordError() {
        if (!this.touched.password) return '';
        if (!this.$v.form.password.required) return this.$t('profile.auth.required-field');
        return '';
      }
    },
    methods: {
      submit() {
        if (!this.isFormValid || this.isSubmitting) return;

        this.isSubmitting = true;
        this.errorMessage = '';

        loginRequest({
          email: this.form.email,
          password: this.form.password,
          invalidCredentialsMessage: this.$t('profile.auth.invalid-credentials')
        }).then(result => {
          if (!result.isSuccess) {
            this.errorMessage = result.message || this.$t('profile.auth.generic-error');
            this.isSubmitting = false;
            return;
          }
          return applyAuthSuccess(result.data).then(() => {
            this.isSubmitting = false;
            this.$emit('success');
          });
        }).catch(() => {
          this.errorMessage = this.$t('profile.auth.generic-error');
          this.isSubmitting = false;
        });
      }
    }
  }
</script>

<style scoped>
.auth-login__link {
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--merchant-primary-color, #1AA179);
  background: transparent;
  border: none;
  cursor: pointer;
}
.auth-login__submit {
  margin-top: 4px;
}
.auth-login__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  font-size: 0.75rem;
  color: #6B7886;
}
.auth-login__divider::before,
.auth-login__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #E0E8EF;
}
</style>



// WEBPACK FOOTER //
// src/components/v2/auth/views/LoginView.vue